import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'

import {
  ensureWeightForSingleSection,
  hasExplicitScoreValue,
  hasOptionScore,
  hasPositiveOptionScore,
  hasPositiveScoreValue,
  hasRangeScore,
  hasSelectedCheckboxScoringOption,
  isCheckboxQuestion,
  isNumberQuestion,
  isOptionQuestion,
  isScoringEligibleQuestion,
  isScaleQuestion,
  isTextLikeQuestion
} from './scoringShared'
import { toNullableNumber } from '../utils'
import { hasOrphanQuestions } from './sectionCompatibility'

function isNegative(value) {
  const parsedValue = toNullableNumber(value)
  return parsedValue !== null && parsedValue < 0
}

function isNotInteger(value) {
  const parsedValue = toNullableNumber(value)

  if (parsedValue === null) {
    return false
  }

  return !Number.isInteger(parsedValue)
}

function isRangeOrderInvalid({ min, max }) {
  const parsedMin = toNullableNumber(min)
  const parsedMax = toNullableNumber(max)

  if (parsedMin === null || parsedMax === null) {
    return false
  }

  return parsedMin > parsedMax
}

function isScaleRangeOutsideQuestionBounds(item) {
  if (!isScaleQuestion(item)) {
    return false
  }

  const questionMin = toNullableNumber(item?.min)
  const questionMax = toNullableNumber(item?.max)
  const scoringMin = toNullableNumber(item?.scoring?.range?.min)
  const scoringMax = toNullableNumber(item?.scoring?.range?.max)

  if (
    questionMin === null ||
    questionMax === null ||
    scoringMin === null ||
    scoringMax === null
  ) {
    return false
  }

  return scoringMin < questionMin || scoringMax > questionMax
}

function isRequiredTextLikeQuestionMissingPoints(item, value) {
  return (
    isTextLikeQuestion(item) && item?.isRequired && !hasExplicitScoreValue(value)
  )
}

function isRequiredOptionQuestionMissingPoints(item) {
  if (!isOptionQuestion(item) || !item?.isRequired) {
    return false
  }

  return !hasOptionScore(item)
}

function isRequiredCheckboxQuestionMissingPoints(item) {
  return (
    isCheckboxQuestion(item) &&
    item?.isRequired &&
    (!hasSelectedCheckboxScoringOption(item) ||
      !hasExplicitScoreValue(item?.scoring?.pointsIfAllSelected))
  )
}

function isRequiredRangeQuestionMissingPoints(item, isRangeQuestion) {
  return (
    isRangeQuestion(item) &&
    item?.isRequired &&
    !hasRangeScore({
      min: item?.scoring?.range?.min,
      max: item?.scoring?.range?.max,
      pointsIfWithinRange: item?.scoring?.pointsIfWithinRange,
    })
  )
}

function getRequiredQuestionPointsChecks(item, value) {
  return [
    isRequiredTextLikeQuestionMissingPoints(item, value),
    isRequiredOptionQuestionMissingPoints(item),
    isRequiredCheckboxQuestionMissingPoints(item),
    isRequiredRangeQuestionMissingPoints(item, isNumberQuestion),
    isRequiredRangeQuestionMissingPoints(item, isScaleQuestion),
  ]
}

function createRequiredQuestionPointsRule({ item, translate }) {
  return value => {
    if (getRequiredQuestionPointsChecks(item, value).some(Boolean)) {
      return translate('validation_required_points')
    }

    return true
  }
}

function createRangeRule({ item, translate }) {
  return () => {
    const scaleGroupErrorKey = getScaleGroupErrorKey(item)

    if (scaleGroupErrorKey === 'validation_scale_range_bounds') {
      return true
    }

    return scaleGroupErrorKey ? translate(scaleGroupErrorKey) : true
  }
}

function getScaleGroupErrorChecks(item) {
  return [
    {
      condition: isRangeOrderInvalid(item?.scoring?.range || {}),
      errorKey: 'validation_invalid_range',
    },
    {
      condition: isScaleRangeOutsideQuestionBounds(item),
      errorKey: 'validation_scale_range_bounds',
    },
  ]
}

function getScaleGroupErrorKey(item) {
  if (!isScaleQuestion(item)) {
    return null
  }

  const failedCheck = getScaleGroupErrorChecks(item).find(
    ({ condition }) => condition
  )

  if (failedCheck) {
    return failedCheck.errorKey
  }

  return null
}

function createNonNegativeRule({ translate }) {
  return value => !isNegative(value) || translate('validation_non_negative')
}

function createIntegerPointsRule({ translate }) {
  return value => !isNotInteger(value) || translate('validation_integer_points')
}

function hasValidRangeQuestionScore(item) {
  if (isRangeOrderInvalid(item?.scoring?.range || {})) {
    return false
  }

  if (isScaleRangeOutsideQuestionBounds(item)) {
    return false
  }

  return hasRangeScore({
    min: item?.scoring?.range?.min,
    max: item?.scoring?.range?.max,
    pointsIfWithinRange: item?.scoring?.pointsIfWithinRange,
  })
}

function hasPositiveQuestionScore(item) {
  if (!isScoringEligibleQuestion(item)) {
    return false
  }

  if (isTextLikeQuestion(item)) {
    return hasPositiveScoreValue(item?.scoring?.pointsIfAnswered)
  }

  if (isOptionQuestion(item)) {
    return hasPositiveOptionScore(item)
  }

  if (isCheckboxQuestion(item)) {
    return (
      hasSelectedCheckboxScoringOption(item) &&
      hasPositiveScoreValue(item?.scoring?.pointsIfAllSelected)
    )
  }

  if (isNumberQuestion(item) || isScaleQuestion(item)) {
    return (
      hasValidRangeQuestionScore(item) &&
      hasPositiveScoreValue(item?.scoring?.pointsIfWithinRange)
    )
  }

  return false
}

function hasPositiveQuestionMaxPoints(item) {
  return hasPositiveQuestionScore(item)
}

function createQuestionRules({ item, translate }) {
  return [
    createRequiredQuestionPointsRule({ item, translate }),
    createRangeRule({ item, translate }),
    createNonNegativeRule({ translate }),
    createIntegerPointsRule({ translate }),
  ]
}

function createSectionWeightRules({ translate }) {
  return [
    value => !isNegative(value) || translate('validation_non_negative'),
  ]
}

function getQuestionModeErrorKeys(items = []) {
  const hasAnyQuestionScore = (Array.isArray(items) ? items : [])
    .filter(isScoringEligibleQuestion)
    .some(item => hasPositiveQuestionScore(item))

  return hasAnyQuestionScore ? [] : ['validation_at_least_one_scored_question']
}

function getSectionGroups(items = []) {
  const groups = []
  const checklistItems = Array.isArray(items) ? items : []

  checklistItems.forEach(item => {
    if (item?.type === ITEM_TYPES.SEPARATOR) {
      groups.push({
        section: item,
        questions: [],
      })

      return
    }

    groups.at(-1)?.questions.push(item)
  })

  return groups
}

function getSectionModeErrorKeys(items = []) {
  const sectionGroups = getSectionGroups(ensureWeightForSingleSection(items))

  if (!sectionGroups.length) {
    return []
  }

  const hasSectionWithoutScoredQuestion = sectionGroups.some(
    ({ section, questions }) => {
      const sectionWeight = toNullableNumber(section?.scoring?.weightPercentage) || 0

      if (sectionWeight === 0) {
        return false
      }

      return !questions
        .filter(isScoringEligibleQuestion)
        .some(question => hasPositiveQuestionMaxPoints(question))
    }
  )

  if (hasSectionWithoutScoredQuestion) {
    return ['validation_section_requires_scored_question']
  }

  const separatorWeights = sectionGroups.map(
    ({ section }) => toNullableNumber(section?.scoring?.weightPercentage) || 0
  )

  const totalWeight = separatorWeights.reduce(
    (accumulator, weight) => accumulator + weight,
    0
  )

  return totalWeight === 100 ? [] : ['validation_weight_sum']
}

function getQuestionModeValidationErrors(items = []) {
  return getQuestionModeErrorKeys(items)
}

function getSectionModeValidationErrors(items = []) {
  return hasOrphanQuestions(items)
    ? ['validation_orphan_questions']
    : getSectionModeErrorKeys(items)
}

export {
  createQuestionRules,
  createSectionWeightRules,
  getQuestionModeValidationErrors,
  getScaleGroupErrorKey,
  getQuestionModeErrorKeys,
  getSectionModeValidationErrors,
  getSectionModeErrorKeys
}
